import axios from "axios";
import { AuthData, Organization, User } from "../contexts/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Cookie helper functions
const setCookie = (name: string, value: string, hours: number = 1) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + hours * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;secure;samesite=strict`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

class AuthService {
  private token: string | null;
  private user: User | null;
  private org: Organization | null;

  constructor() {
    this.token = getCookie("access_token");
    const userCookie = getCookie("user");
    const orgCookie = getCookie("org");
    this.user = userCookie ? JSON.parse(userCookie) : null;
    this.org = orgCookie ? JSON.parse(orgCookie) : null;
  }

  // Sign up with email and password
  async signUp(userData: {
    email: string;
    password: string;
    name: string;
  }): Promise<AuthData> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
        ...userData,
        orgName: "My Organization", // Default org name
        orgSlug: userData.email
          .split("@")[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, ""),
        firstName: userData.name.split(" ")[0],
        lastName: userData.name.split(" ").slice(1).join(" ") || "",
      });
      this.setAuthData(response.data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Sign up failed");
    }
  }

  // Sign in with email and password
  async signIn(credentials: {
    email: string;
    password: string;
  }): Promise<AuthData> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/signin`,
        credentials
      );
      this.setAuthData(response.data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Sign in failed");
    }
  }

  // Sign in with Google
  async signInWithGoogle(
    googleToken: string,
    orgData: any = null
  ): Promise<AuthData> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google`, {
        token: googleToken,
        ...orgData,
      });
      this.setAuthData(response.data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Google sign in failed");
    }
  }

  // Get current user information
  async getMe(): Promise<AuthData> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: this.getAuthHeader(),
        },
      });
      this.setAuthData(response.data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get user information"
      );
    }
  }

  // Set authentication data
  setAuthData(data: AuthData) {
    this.token = data.access_token;
    this.user = data.user;
    this.org = data.org;

    // Store in cookies with 1 hour expiration
    setCookie("access_token", this.token, 1);
    setCookie("user", JSON.stringify(this.user), 1);
    setCookie("org", JSON.stringify(this.org), 1);
  }

  // Clear authentication data
  logout() {
    this.token = null;
    this.user = null;
    this.org = null;

    deleteCookie("access_token");
    deleteCookie("user");
    deleteCookie("org");
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Get authorization header
  getAuthHeader(): string | null {
    return this.token ? `Bearer ${this.token}` : null;
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.user;
  }

  // Get current organization
  getCurrentOrg(): Organization | null {
    return this.org;
  }
}

const authService = new AuthService();
export default authService;
